/**
 * SmartBoothSelector - Shows recommended booths based on vendor preferences
 * Issue #677 - Vendor Booth Preferences & Smart Matching
 * Issue #791 - Added tooltip explanations for match criteria
 */

import React, { useState, useEffect } from 'react';
import { Sparkles, DollarSign, MapPin, Zap, Loader2, AlertCircle, TrendingUp, Info } from 'lucide-react';
import { boothPreferencesApi } from '../services/api';
import { BoothMatchResult } from '../types';
import { getErrorMessage } from '../utils/errorUtils';
import BoothMatchIndicator from './BoothMatchIndicator';
import Tooltip from './Tooltip';
import { formatCurrency } from '../utils/currency'; // Issue #905: Decimal.js utilities

interface SmartBoothSelectorProps {
  fairId: number;
  vendorId: number;
  onSelectBooth?: (slotId: number) => void;
  onViewAllSlots?: () => void;
}

export default function SmartBoothSelector({
  fairId,
  vendorId,
  onSelectBooth,
  onViewAllSlots,
}: SmartBoothSelectorProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [matches, setMatches] = useState<BoothMatchResult[]>([]);

  useEffect(() => {
    const loadMatches = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get matches using vendor's saved preferences
        const results = await boothPreferencesApi.matchPreferences(fairId, {
          vendor_id: vendorId,
        });

        setMatches(results);
      } catch (err: unknown) {
        const errorMsg = getErrorMessage(err);
        setError(errorMsg);
      } finally {
        setLoading(false);
      }
    };

    loadMatches();
  }, [fairId, vendorId]);

  // Helper function to generate tooltip explanations for match reasons (Issue #791)
  const getMatchReasonTooltip = (reason: string): string => {
    const lowerReason = reason.toLowerCase();

    // Power-related reasons
    if (lowerReason.includes('required power outlet') || lowerReason.includes('has required power')) {
      return 'This booth includes an electrical power outlet, which you specified as a requirement in your preferences.';
    }
    if (lowerReason.includes('power outlet available (bonus)')) {
      return 'This booth has a power outlet available, which may be useful even though you did not specify it as a requirement.';
    }
    if (lowerReason.includes('missing required power')) {
      return 'This booth does not have a power outlet, which you marked as required. This booth may not meet your needs.';
    }

    // Dimension-related reasons
    if (lowerReason.includes('meets dimension requirement')) {
      return 'The booth size meets or exceeds your minimum dimension requirements based on your preferences.';
    }
    if (lowerReason.includes('dimensions too small')) {
      return 'This booth is smaller than your specified minimum dimensions and may not provide enough space.';
    }

    // Budget-related reasons
    if (lowerReason.includes('within budget')) {
      return 'The booth price falls within your specified budget range, making it financially suitable.';
    }
    if (lowerReason.includes('below budget')) {
      return 'This booth costs less than your minimum budget, potentially offering good value or extra savings.';
    }
    if (lowerReason.includes('over budget')) {
      return 'This booth exceeds your maximum budget. Consider if the location or features justify the extra cost.';
    }

    // Location preference reasons
    if (lowerReason.includes('corner booth')) {
      return 'This is a corner booth with visibility from two sides, matching your stated preference for corner locations.';
    }
    if (lowerReason.includes('likely corner location')) {
      return 'Based on the booth number, this appears to be a corner location, which you preferred in your settings.';
    }
    if (lowerReason.includes('high-traffic area')) {
      return 'This booth is in a premium or high-traffic location, matching your preference for maximum visibility.';
    }
    if (lowerReason.includes('near entrance')) {
      return 'This booth is located near the fair entrance, providing high visibility as visitors enter, which you indicated as a preference.';
    }

    // Default fallback
    return 'This booth matches one of your saved preferences. Hover over the match score above for more details.';
  };

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-8">
        <div className="flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-secondary" />
          <span className="ml-2 text-gray-600">Finding your perfect booth...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-red-900">Unable to load recommendations</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (matches.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="text-center">
          <Sparkles className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No matching booths found</h3>
          <p className="text-sm text-gray-600 mb-4">
            We couldn't find any available booths matching your preferences at this fair.
          </p>
          {onViewAllSlots && (
            <button
              onClick={onViewAllSlots}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
            >
              View All Available Booths
            </button>
          )}
        </div>
      </div>
    );
  }

  // Show top 5 matches
  const topMatches = matches.slice(0, 5);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-5 h-5 text-secondary" />
        <h3 className="text-lg font-semibold text-gray-900">Recommended Booths For You</h3>
      </div>

      <p className="text-sm text-gray-600 mb-6">
        Based on your saved preferences, here are the best matching booths at this fair:
      </p>

      <div className="space-y-4">
        {topMatches.map((match, index) => (
          <div
            key={match.slot.id}
            className="border border-gray-200 rounded-lg p-4 hover:border-teal-300 hover:shadow-md transition-all"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg font-semibold text-gray-900">
                    Booth #{match.slot.slot_number}
                  </span>
                  {index === 0 && (
                    <span className="px-2 py-0.5 bg-moonbone text-ink text-xs font-medium rounded-full flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      Top Match
                    </span>
                  )}
                </div>

                {/* Booth Details */}
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-3">
                  {match.slot.dimensions && (
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {match.slot.dimensions}
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    <DollarSign className="w-4 h-4" />
                    {formatCurrency(match.slot.price_override || match.slot.base_price)}/day {/* Issue #905: Decimal.js formatting */}
                  </div>
                  {match.slot.has_power && (
                    <div className="flex items-center gap-1 text-green-600">
                      <Zap className="w-4 h-4" />
                      Power available
                    </div>
                  )}
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                    {match.slot.slot_type}
                  </span>
                </div>

                {/* Match Reasons with Tooltips (Issue #791) */}
                {match.match_reasons.length > 0 && (
                  <div className="space-y-1">
                    {match.match_reasons.map((reason, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-xs text-gray-700">
                        <span className="text-green-600 mt-0.5">✓</span>
                        <span className="flex-1">{reason}</span>
                        <Tooltip content={getMatchReasonTooltip(reason)} position="right" allowWrap maxWidth="220px">
                          <button
                            type="button"
                            className="flex-shrink-0 mt-0.5 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-dusty-teal focus:ring-offset-1 rounded"
                            aria-label={`More information about: ${reason}`}
                          >
                            <Info className="w-3.5 h-3.5" />
                          </button>
                        </Tooltip>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Match Score */}
              <div className="flex flex-col items-end gap-3">
                <BoothMatchIndicator score={match.match_score} size="large" />
                {onSelectBooth && (
                  <button
                    onClick={() => onSelectBooth(match.slot.id)}
                    className="px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary-dark transition-colors"
                  >
                    Select
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* View All Button */}
      {matches.length > 5 && (
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600 mb-3">
            {matches.length - 5} more booth{matches.length - 5 !== 1 ? 's' : ''} match your preferences
          </p>
          {onViewAllSlots && (
            <button
              onClick={onViewAllSlots}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              View All Available Booths
            </button>
          )}
        </div>
      )}
    </div>
  );
}
